import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, Leaf, Ban, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRUST = [
  { icon: Leaf, label: "Vegetarian & Non-GMO" },
  { icon: ShieldCheck, label: "GMP Certified" },
  { icon: Ban, label: "No Proprietary Blends" },
];

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    setNotFound(false);
    setActiveImg(0);
    axios.get(`${API}/products/${slug}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="min-h-screen pt-[110px] flex flex-col items-center justify-center gap-5 px-6">
        <h1 className="text-3xl font-bold text-ink">Product not found</h1>
        <Link to="/products" className="text-rose font-semibold" data-testid="back-to-collection">Back to all products</Link>
      </main>
    );
  }
  if (!product) {
    return (
      <main className="min-h-screen pt-[110px] flex items-center justify-center">
        <div className="h-9 w-9 rounded-full border-2 border-gray-200 border-t-ink animate-spin" />
      </main>
    );
  }

  const accent = product.accent;
  const gallery = product.gallery || [];

  return (
    <main data-testid="product-detail-page" className="pt-[110px] bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-8 lg:py-12">
        <Link to="/products" data-testid="pdp-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-ink transition-colors duration-200 mb-8">
          <ArrowLeft size={15} /> All products
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
          {/* Image gallery */}
          <div className="lg:sticky lg:top-[134px] lg:self-start">
            <div className="relative rounded-2xl overflow-hidden border aspect-square" style={{ backgroundColor: `${accent}12`, borderColor: `${accent}33` }}>
              {gallery.length > 0 ? (
                <img key={activeImg} src={gallery[activeImg]} alt={product.name} className={`w-full h-full ${product.imageFit === "contain" ? "object-contain bg-white p-3" : "object-cover"}`} />
              ) : product.photo ? (
                <img src={product.photo} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div
                  data-testid="pdp-placeholder"
                  className="w-full h-full flex flex-col items-center justify-center px-8 text-center"
                  style={{
                    background: `radial-gradient(circle at 30% 20%, ${accent}22 0%, transparent 55%), radial-gradient(circle at 80% 80%, ${accent}18 0%, transparent 55%), #FDFBF7`,
                  }}
                  role="img"
                  aria-label={product.name}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: accent }}>
                    Femora Life
                  </p>
                  <h3 className="font-display text-6xl lg:text-7xl font-medium tracking-tight text-ink mt-3" style={{ color: accent }}>
                    {product.name}
                  </h3>
                  <p className="mt-4 text-sm text-gray-500 max-w-[16rem] leading-relaxed italic">
                    {product.tagline}
                  </p>
                  <span className="mt-8 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                    <span className="h-px w-6 bg-gray-300" /> Packaging in progress <span className="h-px w-6 bg-gray-300" />
                  </span>
                </div>
              )}
              <span className="absolute top-4 left-4 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-white/90 border border-gray-200" style={{ color: accent }}>
                {product.collection === "life-stage" ? "Life-Stage" : "Everyday Wellness"}
              </span>
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-3" data-testid="product-gallery-thumbs">
                {gallery.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`gallery-thumb-${i}`}
                    className={`rounded-lg overflow-hidden border bg-gray-50 aspect-square transition-colors duration-200 ${
                      activeImg === i ? "border-ink ring-2 ring-ink/15" : "border-gray-200 hover:border-gray-400"
                    }`}
                    aria-label={`View image ${i + 1}`}
                  >
                    <img src={img} alt={`${product.name} view ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Summary */}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: accent }}>{product.concern}</p>
            <h1 className="mt-2 font-display text-5xl lg:text-6xl font-medium tracking-tight text-ink">{product.name}</h1>
            <p className="mt-4 text-lg text-gray-600 leading-relaxed">{product.tagline}</p>

            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm">
              <span className="font-semibold text-ink">{product.serving}</span>
              <span className="h-4 w-px bg-gray-300" />
              <span className="text-gray-500">{product.ingredients.length} active ingredients</span>
              {product.vegan && (
                <span data-testid="vegan-badge" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-bold uppercase tracking-widest px-3 py-1.5">
                  <Leaf size={13} /> 100% Vegan
                </span>
              )}
            </div>

            {/* Key benefits */}
            <div className="mt-7 space-y-3">
              {product.benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`benefit-${i}`}>
                  <span className="shrink-0 mt-0.5 h-5 w-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}>
                    <Check size={12} style={{ color: accent }} />
                  </span>
                  <span className="text-ink font-medium">{b}</span>
                </div>
              ))}
            </div>

            {/* Vegan sourcing story */}
            {product.vegan && product.veganHighlights && (
              <div data-testid="vegan-story" className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50/60 p-6">
                <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-emerald-700">
                  <Leaf size={15} /> Fully plant-powered
                </p>
                <p className="mt-2 text-sm font-semibold text-ink">
                  Every nutrient in this formula comes from a vegan source — here's where the heroes come from:
                </p>
                <ul className="mt-4 space-y-2.5">
                  {product.veganHighlights.map((h, i) => (
                    <li key={i} data-testid={`vegan-highlight-${i}`} className="flex items-start gap-2.5 text-sm text-gray-700 leading-relaxed">
                      <span className="shrink-0 mt-1 h-4 w-4 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Leaf size={10} className="text-emerald-700" />
                      </span>
                      {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="pdp-enquire-cta" className="inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-8 py-3.5 hover:bg-rose transition-colors duration-200">
                Enquire about {product.name} <ArrowRight size={16} />
              </Link>
              <Link to="/products" className="inline-flex items-center rounded-md bg-white text-ink border border-gray-300 font-semibold px-8 py-3.5 hover:border-ink transition-colors duration-200">
                Browse more
              </Link>
            </div>

            {/* Trust badges */}
            <div className="mt-8 flex flex-wrap gap-2 pt-6 border-t border-gray-200">
              {TRUST.map((t) => (
                <span key={t.label} className="inline-flex items-center gap-2 text-sm font-medium text-ink bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200">
                  <t.icon size={15} className="text-rose" /> {t.label}
                </span>
              ))}
            </div>

            {/* Supplement Facts */}
            <div className="mt-8 pt-8 border-t border-gray-200" data-testid="supplement-facts">
              <div className="supp-facts">
                <div className="px-1 py-3 border-b-8 border-ink">
                  <h2 className="text-2xl font-extrabold text-ink tracking-tight">Supplement Facts</h2>
                  <p className="text-sm text-ink mt-1">Serving Size: <span className="font-semibold">{product.serving}</span></p>
                </div>
                {product.capsules && product.capsules.length > 0 ? (
                  product.capsules.map((cap, ci) => (
                    <div key={ci} data-testid={`supp-facts-capsule-${ci}`} className={ci > 0 ? "mt-3" : ""}>
                      <div className="flex items-baseline justify-between px-1 py-3 border-y-2 border-ink" style={{ background: `${accent}12` }}>
                        <p className="text-sm font-extrabold uppercase tracking-wider" style={{ color: accent }}>
                          {cap.label}
                          {cap.weight && <span className="ml-2 text-xs font-semibold text-gray-500 normal-case tracking-normal">({cap.weight})</span>}
                        </p>
                        {cap.purpose && (
                          <p className="hidden sm:block text-xs text-gray-500 italic max-w-[55%] text-right">{cap.purpose}</p>
                        )}
                      </div>
                      {cap.ingredients.map((ing, i) => (
                        <div key={i} data-testid={`ingredient-${ci}-${i}`} className="grid grid-cols-12 gap-2 px-1 py-3 border-b border-gray-300 last:border-b-0">
                          <div className="col-span-7">
                            <p className="font-semibold text-ink leading-tight">{ing.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{ing.form}</p>
                            {ing.vegan && (
                              <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                                <Leaf size={10} /> Vegan source
                              </span>
                            )}
                            <p className="text-sm text-gray-600 mt-1.5 leading-snug">{ing.benefit}</p>
                          </div>
                          <div className="col-span-5 text-right">
                            <span data-testid={`ingredient-dose-${ci}-${i}`} className="font-bold whitespace-nowrap" style={{ color: accent }}>{ing.dose}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <>
                    <div className="grid grid-cols-12 px-1 py-2 border-b-2 border-ink text-xs font-bold uppercase tracking-wide text-ink">
                      <div className="col-span-7">Ingredient &amp; Form</div>
                      <div className="col-span-5 text-right">Amount Per Serving</div>
                    </div>
                    {product.ingredients.map((ing, i) => (
                      <div key={i} data-testid={`ingredient-${i}`} className="grid grid-cols-12 gap-2 px-1 py-3 border-b border-gray-300 last:border-b-0">
                        <div className="col-span-7">
                          <p className="font-semibold text-ink leading-tight">{ing.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{ing.form}</p>
                          {ing.vegan && (
                            <span className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                              <Leaf size={10} /> Vegan source
                            </span>
                          )}
                          <p className="text-sm text-gray-600 mt-1.5 leading-snug">{ing.benefit}</p>
                        </div>
                        <div className="col-span-5 text-right">
                          <span data-testid={`ingredient-dose-${i}`} className="font-bold text-ink whitespace-nowrap" style={{ color: accent }}>{ing.dose}</span>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
              {product.otherIngredients ? (
                <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-testid="other-ingredients">
                  <span className="font-semibold text-ink">Other ingredients:</span> {product.otherIngredients}
                </p>
              ) : (
                <p className="text-xs text-gray-400 mt-3">Other ingredients may include vegetarian capsule/sachet base and natural flavour. Daily Values not established for all nutrients.</p>
              )}
            </div>
          </motion.div>
        </div>

        {/* Tabs */}
        <div className="mt-16 lg:mt-24">
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="w-full justify-start h-auto bg-transparent p-0 border-b border-gray-200 rounded-none gap-6 overflow-x-auto no-scrollbar">
              {[
                ["overview", "Overview"],
                ["use", "Suggested Use"],
                ["faq", "FAQ"],
              ].map(([v, label]) => (
                <TabsTrigger
                  key={v}
                  value={v}
                  data-testid={`tab-${v}`}
                  className="rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 pt-0 text-base font-semibold text-gray-400 shadow-none data-[state=active]:border-ink data-[state=active]:text-ink data-[state=active]:shadow-none whitespace-nowrap"
                >
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview" className="pt-8">
              <div className="max-w-3xl">
                <h2 className="text-xl font-bold text-ink mb-3">What it does</h2>
                <p className="text-gray-600 leading-relaxed text-lg">{product.purpose}</p>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-gray-500 mt-10 mb-4">Key benefits</h3>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {product.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={18} className="shrink-0 mt-0.5 text-rose" />
                      <span className="text-ink">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </TabsContent>

            {/* Suggested use */}
            <TabsContent value="use" className="pt-8">
              <div className="max-w-3xl">
                <h2 className="text-xl font-bold text-ink mb-3">How to take {product.name}</h2>
                <p className="text-gray-600 leading-relaxed text-lg">{product.suggestedUse}</p>
                <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-5 flex items-start gap-3">
                  <ShieldCheck size={20} className="text-rose shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-600">If you are pregnant, nursing, taking medication or have a medical condition, consult your healthcare professional before use.</p>
                </div>
              </div>
            </TabsContent>

            {/* FAQ */}
            <TabsContent value="faq" className="pt-8" data-testid="faq-section">
              <div className="max-w-3xl">
                <Accordion type="single" collapsible className="w-full">
                  {product.faq.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                      <AccordionTrigger className="text-left text-ink font-semibold hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-gray-600 text-base leading-relaxed">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
